import { isBefore, isSameDay } from "date-fns";
import { useCallback, useRef, useState } from "react";
import useAsyncEffect from "use-async-effect";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import { getPendingInvoiceStatusReport } from "~/modules/billing/api/pendingInvoice/pendingInvoiceApiDispatchers.ts";
import type { PendingInvoiceStatusReportData } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import type { DispatchStateAction } from "~/types/hookTypes.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";

type Props = {
	invoiceId: string;
	deliveryStartDate: Date;
	deliveryEndDate: Date;
	originalStartDate: Date;
	originalEndDate: Date;
	onPreCheckComplete?: (data: PendingInvoiceStatusReportData) => void;
};

type UsePendingInvoicePreCheckResult = {
	preCheckData: PendingInvoiceStatusReportData | null;
	isPerformingPreCheck: boolean;
	hasConflicts: boolean;
	invoiceTotalIsInvalid: boolean;
	performPreCheck: (startDate: Date, endDate: Date) => Promise<void>;
	setPreCheckData:DispatchStateAction<PendingInvoiceStatusReportData|null>
};

const useInvoiceDetailsByDeliveryPeriod = ({
	invoiceId,
	deliveryStartDate,
	deliveryEndDate,
	originalStartDate,
	originalEndDate,
	onPreCheckComplete,
}: Props): UsePendingInvoicePreCheckResult => {
	const [isPerformingPreCheck, setIsPerformingPreCheck] = useState<boolean>(false);
	const [preCheckData, setPreCheckData] = useState<PendingInvoiceStatusReportData | null>(null);

	const isFetchingRef = useRef(false);
	const onPreCheckCompleteRef = useRef(onPreCheckComplete);
	onPreCheckCompleteRef.current = onPreCheckComplete;

	const performPreCheck = useCallback(
		async (startDate: Date, endDate: Date) => {
			if (isFetchingRef.current) return;
			if (isBefore(endDate, startDate)) return;

			isFetchingRef.current = true;
			setIsPerformingPreCheck(true);

			try {
				const formattedStart = formatDateToYYYYMMDD(startDate);
				const formattedEnd = formatDateToYYYYMMDD(endDate);
				const response = await getPendingInvoiceStatusReport(invoiceId, formattedStart, formattedEnd);
				const data = getDataFromResponse(response);
				setPreCheckData(data);
				onPreCheckCompleteRef.current?.(data);
			} finally {
				isFetchingRef.current = false;
				setIsPerformingPreCheck(false);
			}
		},
		[invoiceId],
	);

	const dateChanged =
		!isSameDay(deliveryStartDate, originalStartDate) ||
		!isSameDay(deliveryEndDate, originalEndDate);

	useAsyncEffect(
		async (isMounted) => {
			if (!dateChanged) {
				setPreCheckData(null);
				setIsPerformingPreCheck(false);
				return;
			}

			await performPreCheck(deliveryStartDate, deliveryEndDate);

			// Guard against state updates after unmount
			if (!isMounted()) return;
		},
		[dateChanged, deliveryEndDate, deliveryStartDate, performPreCheck],
	);

	const hasConflicts = (preCheckData?.conflicts?.length ?? 0) > 0;
	const invoiceTotalIsInvalid = preCheckData?.subtotalCents === 0;

	return {
		preCheckData,
		isPerformingPreCheck,
		hasConflicts,
		invoiceTotalIsInvalid,
		performPreCheck,
		setPreCheckData,
	};
};

export default useInvoiceDetailsByDeliveryPeriod;